import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FaStore } from "react-icons/fa";
import {
  FiMapPin,
  FiPhone,
  FiMail,
  FiUser,
  FiArrowLeft,
  FiSave,
  FiActivity
} from "react-icons/fi";
import axios from "axios";
import Swal from "sweetalert2";

const EditOutletForm = () => {
  const [userRole, setUserRole] = useState(null);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    kota: "",
    phone: "",
    email: "",
    status: "active",
    manager_id: "",
    manager: null,
    address: "",
  });

  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const role = (localStorage.getItem("role") || "").toLowerCase();
    setUserRole(role);
    if (role && !["owner", "admin", "superadmin", "manager"].includes(role)) {
      setShowAccessDenied(true);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token) {
          setError("Token tidak ditemukan. Silakan login ulang.");
          setLoading(false);
          return;
        }

        const outletRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/v1/outlet/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (outletRes.data.success) {
          const outletData = outletRes.data.data;
          setFormData({
            ...outletData,
            manager_id: outletData.manager_id?.toString() || "",
            manager: outletData.manager || null,
          });

          const employeeRes = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/v1/employee?id_outlet=${id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const employeeList = Array.isArray(employeeRes.data.data)
            ? employeeRes.data.data
            : [];

          setManagers(employeeList);
        }
      } catch (err) {
        console.error("Gagal mengambil data:", err);
        setError("Terjadi kesalahan saat memuat data outlet.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "manager_id" ? (value ? Number(value) : "") : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) {
        Swal.fire("Sesi Berakhir", "Silakan login ulang.", "warning");
        return;
      }

      const formattedData = {
        ...formData,
        manager_id: formData.manager_id ? Number(formData.manager_id) : null,
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/v1/outlet/${id}`,
        formattedData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        await Swal.fire({
          title: "Diperbarui!",
          text: `Data outlet "${formData.name}" berhasil diubah.`,
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
          customClass: { popup: "rounded-3xl" },
        });
        navigate("/enterprise/outlet");
      }
    } catch (err) {
      console.error("Error saat mengupdate outlet:", err);
      Swal.fire({
        title: "Gagal Mengubah Outlet",
        text: err.response?.data?.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-screen">
        <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto my-20 p-6 bg-white rounded-3xl shadow-xl text-center space-y-4">
        <h2 className="text-lg font-bold text-rose-600">Kesalahan Memuat Data</h2>
        <p className="text-xs text-gray-500">{error}</p>
        <button
          onClick={() => navigate("/enterprise/outlet")}
          className="px-4 py-2 rounded-xl bg-teal-600 text-white text-xs font-bold"
        >
          Kembali ke Daftar Outlet
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      {/* Header Form */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/enterprise/outlet")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Edit Outlet: {formData.name}
            </h1>
            <p className="text-xs text-gray-500">
              Perbarui informasi kontak, manajer, atau lokasi outlet ini
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Card Form */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white rounded-3xl p-6 md:p-8 shadow-xl shadow-gray-200/50 border border-gray-100"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nama Outlet */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FaStore className="text-teal-600 w-4 h-4" />
                Nama Outlet
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold"
                required
              />
            </div>

            {/* Kota */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiMapPin className="text-teal-600 w-4 h-4" />
                Kota / Kabupaten
              </label>
              <input
                type="text"
                name="kota"
                value={formData.kota}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold"
                required
              />
            </div>

            {/* Telepon */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiPhone className="text-teal-600 w-4 h-4" />
                Nomor Telepon
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold"
                required
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiMail className="text-teal-600 w-4 h-4" />
                Alamat Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold"
                required
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiActivity className="text-teal-600 w-4 h-4" />
                Status Outlet
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                required
              >
                <option value="active">Active (Operasional)</option>
                <option value="closed">Closed (Tutup sementara)</option>
              </select>
            </div>

            {/* Manager Selection */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiUser className="text-teal-600 w-4 h-4" />
                Pilih Manajer Outlet
              </label>
              <select
                name="manager_id"
                value={formData.manager_id || ""}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
              >
                <option value="">-- Pilih Manajer --</option>
                {managers.map((manager) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.full_name || manager.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Alamat Lengkap */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
              <FiMapPin className="text-teal-600 w-4 h-4" />
              Alamat Lengkap
            </label>
            <textarea
              name="address"
              rows={3}
              value={formData.address}
              onChange={handleChange}
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold"
              required
            />
          </div>

          {/* Submit Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate("/enterprise/outlet")}
              className="px-5 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
            >
              Batal
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white text-xs font-bold shadow-lg shadow-teal-600/20 transition-all flex items-center gap-2"
            >
              <FiSave className="w-4 h-4" />
              <span>{isSubmitting ? "Menyimpan..." : "Simpan Perubahan"}</span>
            </motion.button>
          </div>
        </form>
      </motion.div>

      {/* Access Denied Overlay */}
      {showAccessDenied && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded-3xl shadow-2xl w-full max-w-sm text-center space-y-4">
            <h2 className="text-xl font-bold text-gray-900">Akses Ditolak</h2>
            <p className="text-xs text-gray-500">
              Hanya Owner yang memiliki izin mengedit data outlet.
            </p>
            <button
              onClick={() => navigate("/enterprise/outlet")}
              className="w-full py-2.5 rounded-2xl bg-teal-600 text-white text-xs font-bold hover:bg-teal-700"
            >
              Kembali
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditOutletForm;
